import tokens from '@kluein/design-tokens';
import { Box, Button, Heading } from '@kluein/klue-ui';
import { Star } from '@phosphor-icons/react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Logo } from 'components/common/logo';
import { getCompanyLogoUrl } from 'utils/logo';

import useCompanyFavorite from '../hooks/useCompanyFavorite';

import type { CompanyFavoriteType } from '../Company.types';

const StyledMainBox = styled(Box)`
  &:hover {
    box-shadow: ${tokens.boxShadow.small};
    border-color: ${tokens.color.neutral[700]};
  }
`;

const StyledLink = styled.a`
  text-decoration: none;
`;

const CompanyFavorite = ({ company }: { company: CompanyFavoriteType }) => {
  const { isFavorite, handleFavorite } = useCompanyFavorite({
    company,
    showUndo: true,
  });
  const { t } = useTranslation(['Common', 'Company']);
  const navigate = useNavigate();
  const favoriteRef = useRef<HTMLDivElement>(null);

  return (
    <StyledMainBox
      width="100%"
      elevation="none"
      background={tokens.color.neutral[100]}
      round="xxsmall"
      border={{ color: tokens.color.neutral.lightgrey.main, size: 'xsmall' }}
    >
      <StyledLink
        href={`/company/${company.id}`}
        onClick={(e) => {
          e.preventDefault();
          if (favoriteRef?.current?.contains(e.target as Node)) {
            return;
          }
          navigate(`/company/${company.id}`);
        }}
      >
        <Box pad={{ vertical: 'medium', left: 'medium', right: 'small' }}>
          <Box gap="small" direction="row" align="center">
            <Logo
              a11yTitle={company.name}
              src={getCompanyLogoUrl(company.domain)}
              size="xlarge"
              border
            />
            <Box direction="row" align="center" gap="small">
              <Heading
                style={{
                  fontSize: tokens.fontSize.headingFont.medium,
                  display: '-webkit-box',
                  lineHeight: '1.1em',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                }}
                margin="none"
                color={tokens.color.neutral[1000]}
                level="2"
              >
                {company.name}
              </Heading>
            </Box>
            <Box ref={favoriteRef} margin={{ left: 'auto' }} flex={false}>
              <Button
                a11yTitle={t('Common:actions.favorite')}
                variant="plain"
                icon={
                  isFavorite ? (
                    <Star
                      size={tokens.iconSize.small}
                      weight="fill"
                      color={tokens.color.highlight[800]}
                    />
                  ) : (
                    <Star size={tokens.iconSize.small} />
                  )
                }
                onClick={handleFavorite}
              />
            </Box>
          </Box>
        </Box>
      </StyledLink>
    </StyledMainBox>
  );
};

export default CompanyFavorite;
